import React, { useState, useEffect } from 'react';
import { useBusinessPlan } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { usePlanningYears } from '../hooks/usePlanningYears';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Calculator, RefreshCw, TrendingUp, DollarSign, BarChart3, AlertTriangle, Building2, Target, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from './ui/badge';
import { TooltipLabel } from './ui/TooltipLabel';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { advancedCalculationsTooltips } from '../utils/advancedCalculationsTooltips';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdvancedCalculations = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const planningData = usePlanningYears(currentPlan?.id);
  const { years, loading: yearsLoading } = planningData;
  
  const [financialData, setFinancialData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [breakEvenData, setBreakEvenData] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("financial"); // Track active tab
  const [waitingForPlan, setWaitingForPlan] = useState(true);
  
  // Year selector - use first year from planning data as default
  const [selectedYear, setSelectedYear] = useState(years.length > 0 ? years[0] : 2025);

  // Update selectedYear when years load
  useEffect(() => {
    if (years.length > 0 && !years.includes(selectedYear)) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  // Wait for plan to be available (with timeout)
  useEffect(() => {
    if (currentPlan?.id) {
      setWaitingForPlan(false);
    } else {
      // Give it a few seconds for the context to load
      const timeout = setTimeout(() => {
        setWaitingForPlan(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [currentPlan?.id]);
  
  // Format currency with conversion
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return formatCurrencyContext(0, currentPlan?.currency || 'USD');
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, false);
  };

  // Fetch all calculations
  const fetchCalculations = async () => {
    if (!currentPlan?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const [financialRes, cashFlowRes, breakEvenRes, roiRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${selectedYear}`),
        axios.get(`${BACKEND_URL}/api/v3/calculations/cash-flow/${currentPlan.id}?year=${selectedYear}`),
        axios.get(`${BACKEND_URL}/api/v3/calculations/break-even/${currentPlan.id}?year=${selectedYear}`),
        axios.get(`${BACKEND_URL}/api/v3/calculations/roi-analysis/${currentPlan.id}?year=${selectedYear}`)
      ]);

      setFinancialData(financialRes.data);
      setCashFlowData(cashFlowRes.data);
      setBreakEvenData(breakEvenRes.data);
      setRoiData(roiRes.data);
      
      console.log('Break-Even Data:', breakEvenRes.data);
      console.log('ROI Data (NEW STRUCTURE):', roiRes.data);
      console.log('ROI Objectives:', roiRes.data?.objective_level_roi?.length || 0);
      console.log('ROI Initiatives:', roiRes.data?.initiative_level_roi?.length || 0);
      console.log('ROI Streams:', roiRes.data?.revenue_stream_level_roi?.length || 0);
      console.log('ROI Products:', roiRes.data?.product_level_roi?.length || 0);
      
    } catch (err) {
      console.error('Error fetching calculations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalculations();
  }, [currentPlan?.id, selectedYear]); // Re-fetch when year changes

  if (loading || waitingForPlan) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">
              {waitingForPlan ? 'Loading business plan...' : 'Loading calculations...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <Calculator className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Business Plan Selected</h3>
            <p className="text-slate-600 mb-6">Please select a business plan to view calculations.</p>
          </div>
        </div>
      </div>
    );
  }

  const incomeStatement = financialData?.financial_statements?.income_statement || {};
  const balanceSheet = financialData?.financial_statements?.balance_sheet || {};
  const cashFlow = financialData?.financial_statements?.cash_flow || {};

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Financial Analytics</h1>
          <p className="text-slate-600 mt-1">
            Comprehensive financial analysis and metrics for {currentPlan.company_name || currentPlan.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Year Selector */}
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Year:</Label>
            <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={fetchCalculations} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="financial">Financial Statements</TabsTrigger>
          <TabsTrigger value="breakeven">Break-Even Analysis</TabsTrigger>
          <TabsTrigger value="roi">ROI Analysis</TabsTrigger>
          <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
        </TabsList>

        {/* Financial Statements Tab */}
        <TabsContent value="financial" className="space-y-6">
          {/* Quick Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <TooltipLabel tooltip={advancedCalculationsTooltips.revenue}>
                      <p className="text-sm text-blue-700 font-medium">Revenue ({selectedYear})</p>
                    </TooltipLabel>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(incomeStatement.revenue || 0)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <TooltipLabel tooltip={advancedCalculationsTooltips.grossProfit}>
                      <p className="text-sm text-green-700 font-medium">Gross Profit ({selectedYear})</p>
                    </TooltipLabel>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(incomeStatement.gross_profit || 0)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <TooltipLabel tooltip={advancedCalculationsTooltips.ebitda}>
                      <p className="text-sm text-purple-700 font-medium">EBITDA ({selectedYear})</p>
                    </TooltipLabel>
                    <p className="text-2xl font-bold text-purple-900">{formatCurrency(incomeStatement.ebitda || 0)}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <TooltipLabel tooltip={advancedCalculationsTooltips.netIncome}>
                      <p className="text-sm text-orange-700 font-medium">Net Income ({selectedYear})</p>
                    </TooltipLabel>
                    <p className="text-2xl font-bold text-orange-900">{formatCurrency(incomeStatement.net_income || 0)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Financial Statements */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Income Statement */}
            <Card>
              <CardHeader>
                <CardTitle>Income Statement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-600">Revenue</span>
                  <span className="font-semibold">{formatCurrency(incomeStatement.revenue || 0)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600 pl-4">COGS</span>
                  <span className="text-red-600">({formatCurrency(incomeStatement.cogs || 0)})</span>
                </div>
                <div className="flex justify-between py-2 border-b font-semibold">
                  <span className="text-slate-700">Gross Profit</span>
                  <span>{formatCurrency(incomeStatement.gross_profit || 0)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600 pl-4">Operating Expenses</span>
                  <span className="text-red-600">({formatCurrency(incomeStatement.operating_expenses || 0)})</span>
                </div>
                <div className="flex justify-between py-2 border-b font-semibold">
                  <span className="text-slate-700">EBITDA</span>
                  <span>{formatCurrency(incomeStatement.ebitda || 0)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600 pl-4">Depreciation</span>
                  <span className="text-red-600">({formatCurrency(incomeStatement.depreciation || 0)})</span>
                </div>
                <div className="flex justify-between py-2 border-b font-semibold">
                  <span className="text-slate-700">EBIT</span>
                  <span>{formatCurrency(incomeStatement.ebit || 0)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600 pl-4">Interest</span>
                  <span className="text-red-600">({formatCurrency(incomeStatement.interest || 0)})</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-slate-700">Profit Before Tax</span>
                  <span>{formatCurrency(incomeStatement.profit_before_tax || 0)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600 pl-4">Taxes</span>
                  <span className="text-red-600">({formatCurrency(incomeStatement.taxes || 0)})</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 font-bold text-lg">
                  <span className="text-slate-900">Net Income</span>
                  <span className="text-green-600">{formatCurrency(incomeStatement.net_income || 0)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Balance Sheet */}
            <Card>
              <CardHeader>
                <CardTitle>Balance Sheet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="space-y-2 pb-3 border-b">
                  <p className="font-semibold text-slate-900">Assets</p>
                  <div className="flex justify-between py-1 pl-4">
                    <span className="text-slate-600">Cash</span>
                    <span>{formatCurrency(balanceSheet.cash || 0)}</span>
                  </div>
                  <div className="flex justify-between py-1 pl-4">
                    <span className="text-slate-600">Accounts Receivable</span>
                    <span>{formatCurrency(balanceSheet.accounts_receivable || 0)}</span>
                  </div>
                  <div className="flex justify-between py-1 pl-4">
                    <span className="text-slate-600">Fixed Assets</span>
                    <span>{formatCurrency(balanceSheet.fixed_assets || 0)}</span>
                  </div>
                  <div className="flex justify-between py-2 pl-2 font-semibold border-t">
                    <span>Total Assets</span>
                    <span>{formatCurrency(balanceSheet.total_assets || 0)}</span>
                  </div>
                </div>

                <div className="space-y-2 pb-3 border-b">
                  <p className="font-semibold text-slate-900">Liabilities</p>
                  <div className="flex justify-between py-1 pl-4">
                    <span className="text-slate-600">Accounts Payable</span>
                    <span>{formatCurrency(balanceSheet.accounts_payable || 0)}</span>
                  </div>
                  <div className="flex justify-between py-1 pl-4">
                    <span className="text-slate-600">Loans</span>
                    <span>{formatCurrency(balanceSheet.total_loan_balance || 0)}</span>
                  </div>
                  <div className="flex justify-between py-2 pl-2 font-semibold border-t">
                    <span>Total Liabilities</span>
                    <span>{formatCurrency(balanceSheet.total_liabilities || 0)}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold text-slate-900">Equity</p>
                  <div className="flex justify-between py-1 pl-4">
                    <span className="text-slate-600">Paid-in Capital</span>
                    <span>{formatCurrency(balanceSheet.paid_in_capital || 0)}</span>
                  </div>
                  <div className="flex justify-between py-1 pl-4">
                    <span className="text-slate-600">Retained Earnings</span>
                    <span>{formatCurrency(balanceSheet.retained_earnings || 0)}</span>
                  </div>
                  <div className="flex justify-between py-2 pl-2 font-semibold border-t">
                    <span>Total Equity</span>
                    <span>{formatCurrency(balanceSheet.total_equity || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cash Flow Statement */}
            <Card>
              <CardHeader>
                <CardTitle>Cash Flow Statement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between py-2 border-b">
                  <span className="font-semibold text-slate-700">Operating Activities</span>
                  <span className="font-semibold">{formatCurrency(cashFlow.operating_cash_flow || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-semibold text-slate-700">Investing Activities</span>
                  <span className="font-semibold text-red-600">{formatCurrency(Math.abs(cashFlow.investing_cash_flow || 0))}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-semibold text-slate-700">Financing Activities</span>
                  <span className="font-semibold">{formatCurrency(cashFlow.financing_cash_flow || 0)}</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 font-bold text-lg">
                  <span className="text-slate-900">Net Cash Flow</span>
                  <span className={cashFlow.net_cash_flow >= 0 ? "text-green-600" : "text-red-600"}>
                    {formatCurrency(cashFlow.net_cash_flow || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Break-Even Analysis Tab */}
        <TabsContent value="breakeven" className="space-y-6">
          <BreakEvenAnalysis 
            data={breakEvenData} 
            formatCurrency={formatCurrency}
            planId={currentPlan.id}
            selectedYear={selectedYear}
          />
        </TabsContent>

        {/* ROI Analysis Tab */}
        <TabsContent value="roi" className="space-y-6">
          <ROIAnalysis 
            data={roiData} 
            formatCurrency={formatCurrency}
            planId={currentPlan.id}
          />
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cashflow" className="space-y-6">
          <CashFlowProjection 
            data={cashFlowData} 
            formatCurrency={formatCurrency}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Break-Even Analysis Component
const BreakEvenAnalysis = ({ data, formatCurrency, planId, selectedYear }) => {
  const debugInfo = data?.debug_info || {};
  const analysis = data?.break_even_analysis || {};
  
  // Check if we have the minimum data needed
  const hasProducts = debugInfo.products_with_units_and_price > 0;
  const hasCosts = debugInfo.fixed_cost_items > 0 || debugInfo.variable_cost_items > 0;
  
  if (!hasProducts || !hasCosts) {
    return (
      <Card className="border-yellow-200 bg-yellow-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            Missing Data for Break-Even Analysis
          </CardTitle>
          <CardDescription>
            To calculate break-even point, you need:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {hasProducts ? (
                <Badge className="bg-green-100 text-green-700">✓ Products with pricing</Badge>
              ) : (
                <Badge variant="destructive">✗ Need products with units & price</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasCosts ? (
                <Badge className="bg-green-100 text-green-700">✓ Cost items defined</Badge>
              ) : (
                <Badge variant="destructive">✗ Need cost items (fixed & variable)</Badge>
              )}
            </div>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <p className="text-sm font-semibold mb-2">Current Status:</p>
            <ul className="text-sm space-y-1 text-slate-600">
              <li>• Products found: {debugInfo.products_found || 0}</li>
              <li>• Products with pricing: {debugInfo.products_with_units_and_price || 0}</li>
              <li>• Fixed cost items: {debugInfo.fixed_cost_items || 0}</li>
              <li>• Variable cost items: {debugInfo.variable_cost_items || 0}</li>
            </ul>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => window.location.href = '/products-services'}>
              Add Products
            </Button>
            <Button onClick={() => window.location.href = '/cost-management'} variant="outline">
              Add Costs
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <BarChart3 className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h4 className="font-semibold text-green-900 mb-1">About Break-Even Analysis</h4>
            <p className="text-sm text-green-700">
              All calculations are <strong>annual</strong> based on your current product pricing and cost structure. 
              Break-even point shows the minimum sales volume needed per year to cover all fixed and variable costs. 
              Margin of Safety shows how much cushion you have above break-even.
            </p>
          </div>
        </div>
      </div>
      
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.breakEvenUnits}>
              <p className="text-sm text-blue-700 font-medium">Break-Even Units</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-blue-900">
              {analysis.break_even_units ? Math.ceil(analysis.break_even_units).toLocaleString() : 'N/A'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.breakEvenRevenue}>
              <p className="text-sm text-green-700 font-medium">Break-Even Revenue ({selectedYear})</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-green-900">
              {formatCurrency(analysis.break_even_revenue || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.contributionMarginPercent}>
              <p className="text-sm text-purple-700 font-medium">Contribution Margin ({selectedYear})</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-purple-900">
              {analysis.contribution_margin_percent ? `${analysis.contribution_margin_percent.toFixed(1)}%` : 'N/A'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.marginOfSafety}>
              <p className="text-sm text-orange-700 font-medium">Margin of Safety ({selectedYear})</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-orange-900">
              {analysis.margin_of_safety?.percent ? `${analysis.margin_of_safety.percent.toFixed(1)}%` : 'N/A'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Cost Structure</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-slate-600">Fixed Costs (Total)</span>
              <span className="font-semibold">{formatCurrency(analysis.fixed_costs_total || 0)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-slate-600">Variable Cost per Unit</span>
              <span className="font-semibold">{formatCurrency(analysis.variable_cost_per_unit || 0)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-slate-600">Average Selling Price</span>
              <span className="font-semibold">{formatCurrency(analysis.average_selling_price || 0)}</span>
            </div>
            <div className="flex justify-between py-2 font-semibold">
              <span className="text-slate-700">Contribution Margin per Unit</span>
              <span className="text-green-600">{formatCurrency(analysis.contribution_margin_per_unit || 0)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Current Position</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-slate-600">Current Units Sold</span>
              <span className="font-semibold">
                {analysis.current_position?.current_units?.toLocaleString() || 0}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-slate-600">Current Revenue</span>
              <span className="font-semibold">
                {formatCurrency(analysis.current_position?.current_revenue || 0)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-slate-600">Units Above Break-Even</span>
              <span className={`font-semibold ${(analysis.current_position?.units_above_breakeven || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {analysis.current_position?.units_above_breakeven?.toLocaleString() || 0}
              </span>
            </div>
            <div className="flex justify-between py-2 font-semibold">
              <span className="text-slate-700">Status</span>
              <Badge className={
                analysis.break_even_units === null 
                  ? 'bg-red-100 text-red-700 border-red-300' 
                  : (analysis.current_position?.units_above_breakeven || 0) > 0 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-orange-100 text-orange-700'
              }>
                {analysis.break_even_units === null 
                  ? 'Cannot Break Even (Negative Margin)' 
                  : (analysis.current_position?.units_above_breakeven || 0) > 0 
                    ? 'Above Break-Even' 
                    : 'Below Break-Even'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ROI Analysis Component  
const ROIAnalysis = ({ data, formatCurrency, planId }) => {
  const [activeLevel, setActiveLevel] = useState('objective');

  if (!data) {
    return <div className="text-center py-12 text-gray-500">Loading ROI Analytics...</div>;
  }

  const objectiveROI = data?.objective_level_roi || [];
  const initiativeROI = data?.initiative_level_roi || [];
  const streamROI = data?.revenue_stream_level_roi || [];
  const productROI = data?.product_level_roi || [];
  const summary = data?.analytics_summary || {};
  
  // Check if we have any data
  if (objectiveROI.length === 0 && initiativeROI.length === 0 && streamROI.length === 0 && productROI.length === 0) {
    return (
      <Card className="border-yellow-200 bg-yellow-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            No ROI Data Available
          </CardTitle>
          <CardDescription>
            Create objectives, initiatives, revenue streams, and products in Strategic Framework to see ROI analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => window.location.href = '/strategic-framework'}>
            Go to Strategic Framework
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Get ROI color
  const getRoiColor = (roi) => {
    if (roi >= 20) return 'text-green-600';
    if (roi >= 5) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Portfolio ROI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 font-medium">Total Investment</p>
            <p className="text-2xl font-bold text-blue-900">
              {formatCurrency(
                objectiveROI.reduce((sum, obj) => sum + obj.investment, 0)
              )}
            </p>
            <p className="text-xs text-blue-600 mt-1">Across all objectives</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <p className="text-xs text-green-700 font-medium">Total Revenue</p>
            <p className="text-2xl font-bold text-green-900">
              {formatCurrency(
                objectiveROI.reduce((sum, obj) => sum + obj.revenue, 0)
              )}
            </p>
            <p className="text-xs text-green-600 mt-1">From all objectives</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <p className="text-xs text-purple-700 font-medium">Portfolio Net Profit</p>
            <p className={`text-2xl font-bold ${objectiveROI.reduce((sum, obj) => sum + obj.net_profit, 0) >= 0 ? 'text-purple-900' : 'text-red-900'}`}>
              {formatCurrency(
                objectiveROI.reduce((sum, obj) => sum + obj.net_profit, 0)
              )}
            </p>
            <p className="text-xs text-purple-600 mt-1">Revenue - Operating Costs</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <p className="text-xs text-orange-700 font-medium">Portfolio ROI</p>
            <p className="text-2xl font-bold text-orange-900">
              {((objectiveROI.reduce((sum, obj) => sum + obj.net_profit, 0) / 
                 objectiveROI.reduce((sum, obj) => sum + obj.investment, 0) * 100) || 0).toFixed(1)}%
            </p>
            <p className="text-xs text-orange-600 mt-1">Overall return</p>
          </CardContent>
        </Card>
      </div>

      {/* Best Performers Banner */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-2">🏆 Top ROI Performers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Best Objective</p>
            <p className="font-semibold text-green-700">{summary.best_objective || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-600">Best Initiative</p>
            <p className="font-semibold text-green-700">{summary.best_initiative || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-600">Best Revenue Stream</p>
            <p className="font-semibold text-green-700">{summary.best_stream || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-600">Best Product</p>
            <p className="font-semibold text-green-700">{summary.best_product || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Tabs for Each Level */}
      <Tabs value={activeLevel} onValueChange={setActiveLevel}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="objective">Objectives ({objectiveROI.length})</TabsTrigger>
          <TabsTrigger value="initiative">Initiatives ({initiativeROI.length})</TabsTrigger>
          <TabsTrigger value="stream">Revenue Streams ({streamROI.length})</TabsTrigger>
          <TabsTrigger value="product">Products ({productROI.length})</TabsTrigger>
        </TabsList>

        {/* OBJECTIVE-LEVEL ROI */}
        <TabsContent value="objective" className="space-y-4">
          <div className="bg-blue-50 p-3 rounded border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Objective-Level ROI:</strong> Aggregated ROI from all initiatives under each objective. Shows which strategic objectives deliver the best returns.
            </p>
          </div>
          
          {objectiveROI.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">No objectives with ROI data</CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Objective</th>
                    <th className="px-4 py-3 text-left font-semibold">Pillar</th>
                    <th className="px-4 py-3 text-right font-semibold">Initiatives</th>
                    <th className="px-4 py-3 text-right font-semibold">Investment</th>
                    <th className="px-4 py-3 text-right font-semibold">Revenue</th>
                    <th className="px-4 py-3 text-right font-semibold">Net Profit</th>
                    <th className="px-4 py-3 text-right font-semibold">ROI %</th>
                    <th className="px-4 py-3 text-right font-semibold">Annualized ROI</th>
                    <th className="px-4 py-3 text-right font-semibold">Payback (yrs)</th>
                    <th className="px-4 py-3 text-right font-semibold">NPV (5yr)</th>
                    <th className="px-4 py-3 text-right font-semibold">Prof. Index</th>
                  </tr>
                </thead>
                <tbody>
                  {objectiveROI.map((obj, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{obj.objective_name}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{obj.pillar}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">{obj.initiative_count}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(obj.investment)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(obj.revenue)}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${obj.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(obj.net_profit)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold text-lg ${getRoiColor(obj.roi_metrics.roi_percent)}`}>
                        {obj.roi_metrics.roi_percent.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-right">{obj.roi_metrics.annualized_roi.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-right">
                        {obj.roi_metrics.payback_years ? obj.roi_metrics.payback_years.toFixed(1) : 'N/A'}
                      </td>
                      <td className={`px-4 py-3 text-right ${obj.roi_metrics.npv_5_year >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(obj.roi_metrics.npv_5_year)}
                      </td>
                      <td className="px-4 py-3 text-right">{obj.roi_metrics.profitability_index.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="px-4 py-3" colSpan="6">Average</td>
                    <td className="px-4 py-3 text-right text-lg">{summary.avg_objective_roi?.toFixed(1)}%</td>
                    <td colSpan="4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>

        {/* INITIATIVE-LEVEL ROI */}
        <TabsContent value="initiative" className="space-y-4">
          <div className="bg-purple-50 p-3 rounded border border-purple-200">
            <p className="text-sm text-purple-800">
              <strong>Initiative-Level ROI:</strong> Individual initiative performance. Shows which strategic initiatives are worth continuing or expanding.
            </p>
          </div>
          
          {initiativeROI.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">No initiatives with ROI data</CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Initiative</th>
                    <th className="px-4 py-3 text-left font-semibold">Objective</th>
                    <th className="px-4 py-3 text-right font-semibold">Streams</th>
                    <th className="px-4 py-3 text-right font-semibold">Investment</th>
                    <th className="px-4 py-3 text-right font-semibold">Revenue</th>
                    <th className="px-4 py-3 text-right font-semibold">Net Profit</th>
                    <th className="px-4 py-3 text-right font-semibold">ROI %</th>
                    <th className="px-4 py-3 text-right font-semibold">Annualized ROI</th>
                    <th className="px-4 py-3 text-right font-semibold">Payback (yrs)</th>
                    <th className="px-4 py-3 text-right font-semibold">NPV (5yr)</th>
                    <th className="px-4 py-3 text-right font-semibold">Prof. Index</th>
                  </tr>
                </thead>
                <tbody>
                  {initiativeROI.map((init, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{init.initiative_name}</td>
                      <td className="px-4 py-3 text-gray-600">{init.objective_name}</td>
                      <td className="px-4 py-3 text-right">{init.stream_count}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(init.investment)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(init.revenue)}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${init.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(init.net_profit)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold text-lg ${getRoiColor(init.roi_metrics.roi_percent)}`}>
                        {init.roi_metrics.roi_percent.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-right">{init.roi_metrics.annualized_roi.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-right">
                        {init.roi_metrics.payback_years ? init.roi_metrics.payback_years.toFixed(1) : 'N/A'}
                      </td>
                      <td className={`px-4 py-3 text-right ${init.roi_metrics.npv_5_year >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(init.roi_metrics.npv_5_year)}
                      </td>
                      <td className="px-4 py-3 text-right">{init.roi_metrics.profitability_index.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="px-4 py-3" colSpan="6">Average</td>
                    <td className="px-4 py-3 text-right text-lg">{summary.avg_initiative_roi?.toFixed(1)}%</td>
                    <td colSpan="4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>

        {/* REVENUE STREAM-LEVEL ROI */}
        <TabsContent value="stream" className="space-y-4">
          <div className="bg-green-50 p-3 rounded border border-green-200">
            <p className="text-sm text-green-800">
              <strong>Revenue Stream-Level ROI:</strong> Profitability of each revenue stream. Shows which streams generate the best returns and margins.
            </p>
          </div>
          
          {streamROI.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">No revenue streams with ROI data</CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Revenue Stream</th>
                    <th className="px-4 py-3 text-left font-semibold">Type</th>
                    <th className="px-4 py-3 text-right font-semibold">Products</th>
                    <th className="px-4 py-3 text-right font-semibold">Revenue</th>
                    <th className="px-4 py-3 text-right font-semibold">Margin %</th>
                    <th className="px-4 py-3 text-right font-semibold">Net Profit</th>
                    <th className="px-4 py-3 text-right font-semibold">ROI %</th>
                    <th className="px-4 py-3 text-right font-semibold">Annualized ROI</th>
                    <th className="px-4 py-3 text-right font-semibold">Payback (yrs)</th>
                    <th className="px-4 py-3 text-right font-semibold">NPV (5yr)</th>
                    <th className="px-4 py-3 text-right font-semibold">Prof. Index</th>
                  </tr>
                </thead>
                <tbody>
                  {streamROI.map((stream, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{stream.stream_name}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{stream.stream_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">{stream.product_count}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatCurrency(stream.revenue)}</td>
                      <td className="px-4 py-3 text-right">{stream.margin_percent.toFixed(1)}%</td>
                      <td className={`px-4 py-3 text-right font-semibold ${stream.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(stream.net_profit)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold text-lg ${getRoiColor(stream.roi_metrics.roi_percent)}`}>
                        {stream.roi_metrics.roi_percent.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-right">{stream.roi_metrics.annualized_roi.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-right">
                        {stream.roi_metrics.payback_years ? stream.roi_metrics.payback_years.toFixed(1) : 'N/A'}
                      </td>
                      <td className={`px-4 py-3 text-right ${stream.roi_metrics.npv_5_year >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(stream.roi_metrics.npv_5_year)}
                      </td>
                      <td className="px-4 py-3 text-right">{stream.roi_metrics.profitability_index.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="px-4 py-3" colSpan="6">Average</td>
                    <td className="px-4 py-3 text-right text-lg">{summary.avg_stream_roi?.toFixed(1)}%</td>
                    <td colSpan="4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>

        {/* PRODUCT-LEVEL ROI */}
        <TabsContent value="product" className="space-y-4">
          <div className="bg-orange-50 p-3 rounded border border-orange-200">
            <p className="text-sm text-orange-800">
              <strong>Product-Level ROI:</strong> Individual product profitability with unit economics. Shows which products deliver the best returns.
            </p>
          </div>
          
          {productROI.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-gray-500">No products with ROI data</CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Product</th>
                    <th className="px-4 py-3 text-left font-semibold">SKU</th>
                    <th className="px-4 py-3 text-left font-semibold">Revenue Stream</th>
                    <th className="px-4 py-3 text-right font-semibold">Units</th>
                    <th className="px-4 py-3 text-right font-semibold">Revenue</th>
                    <th className="px-4 py-3 text-right font-semibold">Gross Margin %</th>
                    <th className="px-4 py-3 text-right font-semibold">Net Profit</th>
                    <th className="px-4 py-3 text-right font-semibold">ROI %</th>
                    <th className="px-4 py-3 text-right font-semibold">Annualized ROI</th>
                    <th className="px-4 py-3 text-right font-semibold">Payback (yrs)</th>
                    <th className="px-4 py-3 text-right font-semibold">NPV (5yr)</th>
                    <th className="px-4 py-3 text-right font-semibold">Prof. Index</th>
                  </tr>
                </thead>
                <tbody>
                  {productROI.map((product, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{product.product_name}</td>
                      <td className="px-4 py-3 text-gray-600">{product.product_sku}</td>
                      <td className="px-4 py-3 text-gray-600">{product.revenue_stream_name}</td>
                      <td className="px-4 py-3 text-right">{Math.round(product.units).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatCurrency(product.revenue)}</td>
                      <td className="px-4 py-3 text-right">{product.gross_margin.toFixed(1)}%</td>
                      <td className={`px-4 py-3 text-right font-semibold ${product.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(product.net_profit)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold text-lg ${getRoiColor(product.roi_metrics.roi_percent)}`}>
                        {product.roi_metrics.roi_percent.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-right">{product.roi_metrics.annualized_roi.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-right">
                        {product.roi_metrics.payback_years ? product.roi_metrics.payback_years.toFixed(1) : 'N/A'}
                      </td>
                      <td className={`px-4 py-3 text-right ${product.roi_metrics.npv_5_year >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(product.roi_metrics.npv_5_year)}
                      </td>
                      <td className="px-4 py-3 text-right">{product.roi_metrics.profitability_index.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="px-4 py-3" colSpan="7">Average</td>
                    <td className="px-4 py-3 text-right text-lg">{summary.avg_product_roi?.toFixed(1)}%</td>
                    <td colSpan="4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Cash Flow Projection Component
const CashFlowProjection = ({ data, formatCurrency }) => {
  const projection = data?.cash_flow_projection || [];
  const summary = data?.summary || {};
  
  if (projection.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600">No cash flow projection data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.totalOperatingCF}>
              <p className="text-sm text-blue-700 font-medium">Total Operating CF</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-blue-900">{formatCurrency(summary.total_operating_cash_flow || 0)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.totalCapex}>
              <p className="text-sm text-red-700 font-medium">Total CapEx</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-red-900">{formatCurrency(summary.total_capex || 0)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.finalCashBalance}>
              <p className="text-sm text-green-700 font-medium">Final Cash Balance</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-green-900">{formatCurrency(summary.final_cash_balance || 0)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <TooltipLabel tooltip={advancedCalculationsTooltips.averageCashRunway}>
              <p className="text-sm text-purple-700 font-medium">Avg Cash Runway</p>
            </TooltipLabel>
            <p className="text-2xl font-bold text-purple-900">
              {summary.average_cash_runway_months ? `${Math.round(summary.average_cash_runway_months)} mo` : 'N/A'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 5-Year Projection Table */}
      <Card>
        <CardHeader>
          <CardTitle>5-Year Cash Flow Projection</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b-2">
                <tr className="text-left">
                  <th className="py-2 font-semibold text-slate-700">Year</th>
                  <th className="py-2 font-semibold text-slate-700 text-right">Beginning Cash</th>
                  <th className="py-2 font-semibold text-slate-700 text-right">Operating CF</th>
                  <th className="py-2 font-semibold text-slate-700 text-right">CapEx</th>
                  <th className="py-2 font-semibold text-slate-700 text-right">Net CF</th>
                  <th className="py-2 font-semibold text-slate-700 text-right">Ending Cash</th>
                  <th className="py-2 font-semibold text-slate-700 text-right">Runway (mo)</th>
                </tr>
              </thead>
              <tbody>
                {projection.map((year, idx) => (
                  <tr key={idx} className="border-b hover:bg-slate-50">
                    <td className="py-3 font-medium">{year.year}</td>
                    <td className="py-3 text-right">{formatCurrency(year.beginning_cash || 0)}</td>
                    <td className="py-3 text-right text-green-600">{formatCurrency(year.operating_cash_flow || 0)}</td>
                    <td className="py-3 text-right text-red-600">({formatCurrency(year.capex_spending || 0)})</td>
                    <td className={`py-3 text-right font-semibold ${(year.net_cash_flow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(year.net_cash_flow || 0)}
                    </td>
                    <td className="py-3 text-right font-semibold">{formatCurrency(year.ending_cash || 0)}</td>
                    <td className="py-3 text-right">{year.cash_runway_months ? Math.round(year.cash_runway_months) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedCalculations;
